import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import useAxios from 'axios-hooks'
import { apiClient } from 'utils/api-client'
import { getAuthHeader } from 'utils/auth'
import { ContentContainer, StyledAlert, notificationToast } from 'components'
import { Form } from 'antd'
import BasicInfo from 'views/ScanSettings/NewScan/steps/BasicInfo'
import ScanStep from 'views/ScanSettings/NewScan/steps/ScanStep'
import { HeaderTitle, NewScanFooter, MainActionButton } from 'views/ScanSettings/NewScan/style'
import { Link } from '@reach/router'
import { colors } from 'style/theme'
import { useProjects } from 'context/project-context'
import ValidationErrors from 'views/ScanSettings/NewScan/components/ValidationErrors'
import {
  ALL_SUB_DOMAINS_FIELD,
  CUSTOM_SCAN_FIELD,
  FLOWS,
  FLOW_NAME_FIELD,
  FLOW_ACTIONS,
  FULL_SCAN_COUNT_FIELD,
  FULL_SCAN_ROOT_URL_FIELD,
  SCAN_PREFIX,
  SCAN_NAME_FIELD,
  SCAN_TYPE,
  SCAN_TYPE_CUSTOM,
  SCAN_TYPE_FLOW,
  SCAN_TYPE_FULL
} from 'views/ScanSettings/NewScan/constants'
import { StyledNewScanContainer } from 'views/ScanSettings/NewScan/style'
import {usePayment} from 'context/payment-context';
import Help from 'views/ScanSettings/NewScan/components/Help'

const StyledNewSimpleScan = styled.div({
  width: 774
})

const SimpleScanTop = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 182px',
  alignItems: 'center',
  padding: '30px 50px',
  h1: {
    margin: 0,
    padding: 0
  }
})

const SimpleScanContent = styled.div({
  padding: '30px 50px',
  '.ant-tabs-bar': {
    display: 'none'
  }
})

const SaveButton = styled(MainActionButton)({
  background: colors.brand,
  border: 'none',
  ':hover, :active, :focus': {
    background: '#5bb4ff'
  }
})

const flowsURL = '/global/flows'

const NewSimpleScan = ({ form, navigate }) => {
  const { project } = useProjects()
  const [error, setError] = useState(null)
  const {setPaymentModalOpen, isCanceledPayg} = usePayment();


  const remoteValidationBaseUrl = `/${project.name}/scan-settings/validate/`

  const [{ data: flows, loading: loadingFlows, error: flowsError }] = useAxios({
    url: flowsURL,
    method: 'GET',
    headers: getAuthHeader()
  })

  const fetchSimpleScan = async (body, isSaveWithRun) => 
  {
      try
      {
        let simpleScanData = await apiClient(`/${project.name}/scan-settings/simple`, { method: 'POST', body, headers: getAuthHeader() })
        if (simpleScanData) 
        {
          if (isSaveWithRun) {
            await runScan(simpleScanData);
          }
          else{
            navigate(`../../scan-settings`)
            notificationToast({
              message: 'Success',
              description: 'Simple scan created',
              icontype: 'check'
            }) 
          }
        }
      }
      catch(err){
        err = err.response && err.response.data && err.response.data.Errors;
        setError(err);
      }
  }

  const runScan = async (simpleScanData) =>{
    try{
      await apiClient(`/${project.name}/scan-settings/scans`, {
        method: 'POST',
        body: { scan_settings_id: simpleScanData.data.id },
        headers: getAuthHeader()
      })
      navigate(`../../scan-settings/${simpleScanData.data.id}/scans/`)
      notificationToast({
        message: 'Success',
        description: 'Simple scan created',
        icontype: 'check'
      }) 
    }
    catch(err){
      let createScanError = err.response && err.response.data && err.response.data.Errors;
      navigate(`../../scan-settings`)
      notificationToast({
            message: 'Error',
            description: createScanError,
            icontype: 'alert'
      }) 
    }
  }

  const remoteValidation = (endpoint, body) => {
    return apiClient(remoteValidationBaseUrl + endpoint, { method: 'POST', body: body, headers: getAuthHeader() })
  }
  
  const save = async (isSaveWithRun) => {
    try {
      let payload = {}

      if (isSaveWithRun && isCanceledPayg) {
        setPaymentModalOpen(true)
        return;
      }

      const scanType = form.getFieldValue(SCAN_TYPE)
      const values = await form.validateFields([SCAN_NAME_FIELD])

      payload.name = values[SCAN_NAME_FIELD]
      payload.type = scanType
      //=========================== FULL SCAN ============================
      if (scanType === SCAN_TYPE_FULL) {
        const fullScanValues = await form.validateFields([
          FULL_SCAN_ROOT_URL_FIELD,
          FULL_SCAN_COUNT_FIELD,
          ALL_SUB_DOMAINS_FIELD
        ])
        const rootUrl = fullScanValues[FULL_SCAN_ROOT_URL_FIELD]
        const count = fullScanValues[FULL_SCAN_COUNT_FIELD]
        const withSubDomains = fullScanValues[ALL_SUB_DOMAINS_FIELD]

        let fullScanValidationBody = {
          withSubDomains: withSubDomains,
          root_url: rootUrl,
          count: count,
          scan_settings_name: values[SCAN_NAME_FIELD]
        }

        payload.root_url = rootUrl
        payload.count = count
        payload.include_subdomains = withSubDomains
      }
      //=========================== CUSTOM SCAN ============================
      if (scanType === SCAN_TYPE_CUSTOM) {
        const customScanValues = await form.validateFields([CUSTOM_SCAN_FIELD])

        const pages = customScanValues[CUSTOM_SCAN_FIELD].replace(/^\s*[\r\n]/gm, '').split(/\n/)

        let customScanValidationBody = {
          scan_settings_name: values[SCAN_NAME_FIELD],
          pages: pages
        }

        payload.type = 'list'
        payload.pages = pages
      }
      //=========================== FLOW SCAN ============================
      if (scanType === SCAN_TYPE_FLOW) {
        const flowScan = form.getFieldValue(`${SCAN_PREFIX}${FLOWS}`)
        if (!flowScan[0]) {
          setError(['Save flow is required'])
          return
        } else {
          if (flowScan[0].id) {
            payload.flow = flowScan[0].id
          } else {
            payload['new_flow'] = {
              name: flowScan[0][`${SCAN_PREFIX}${FLOW_NAME_FIELD}`],
              actions: flowScan[0][`${SCAN_PREFIX}${FLOW_ACTIONS}`]
            }
          }
        }
      }
      //====================================================================
      fetchSimpleScan(payload, isSaveWithRun)
      //====================================================================
    } catch (error) {
      if (error.response && error.response.data && error.response.data.Errors) {
        setError(error.response.data.Errors)
      }
    }
  }

  return (
    <StyledNewScanContainer>
      <StyledNewSimpleScan>
        <ContentContainer pad="none">
          <SimpleScanTop>
            <HeaderTitle>NEW SCAN SETTINGS</HeaderTitle>
            <Link id="switchToAdvanced" to="../new-scan">
              <strong>SWITCH TO ADVANCED MODE</strong>
            </Link>
          </SimpleScanTop>
          <SimpleScanContent>
            {error && (
              <StyledAlert
                style={{ marginBottom: 20 }}
                message="Error"
                description={<ValidationErrors errors={error} />}
                type="error"
                showIcon
              />
            )}
            <Form layout="vertical">
              <BasicInfo form={form} />
              <ScanStep form={form} setValidationError={setError} flows={flows} simpleMode={true} />
            </Form>
          </SimpleScanContent>
          <NewScanFooter>
            <SaveButton id="saveScan" type="primary" size="large" onClick={() => save(false)}>
              SAVE
            </SaveButton>
            <MainActionButton
              id="saveAndRunScan"
              size="large"
              type="primary"
              onClick={() => {
                save(true)
              }}
            >
              SAVE & RUN
            </MainActionButton>
          </NewScanFooter>
        </ContentContainer>
      </StyledNewSimpleScan>
      <ContentContainer>
        <Help form={form} currentStep="simple" />
      </ContentContainer>
    </StyledNewScanContainer>
  )
}

export default Form.create({})(NewSimpleScan)
